import React, { useState } from 'react';
import { Building2, Network, Calendar, Download, Search } from 'lucide-react';
import ByOperatorView from './ByOperatorView';
import ByServiceView from './ByServiceView';
import PDFExporter from '../utils/pdfExporter';

const Dashboard = () => {
  const [activeView, setActiveView] = useState('operators');
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [appliedMonth, setAppliedMonth] = useState(now.getMonth() + 1);
  const [appliedYear, setAppliedYear] = useState(now.getFullYear());
  const [isExporting, setIsExporting] = useState(false);

  const handleSearchClick = () => {
    setAppliedMonth(selectedMonth);
    setAppliedYear(selectedYear);
  };

  const tabs = [
    {
      id: 'operators',
      name: 'By Operators',
      icon: Building2,
      description: 'View regulatory data organized by telecom operators'
    },
    {
      id: 'services',
      name: 'By Services',
      icon: Network,
      description: 'View regulatory data organized by service categories'
    }
  ];

  // Month options for filtering
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const selectedMonthName = months.find(m => m.value === appliedMonth)?.label || 'Current';
      const filename = `telecom-regulatory-dashboard-${activeView}-${selectedMonthName.toLowerCase()}`;

      const result = await PDFExporter.exportToPDF(
        'telecom-regulatory-dashboard',
        filename,
        {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        }
      );

      if (result.success) {
        console.log('PDF exported successfully');
      } else {
        console.error('PDF export failed:', result.error);
        alert('Failed to export PDF: ' + result.error);
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div id="telecom-regulatory-dashboard" className="space-y-6">
      {/* Filters and actions (title is in DashboardRegulatory PageHeader) */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden p-4">
        <div className="flex flex-col gap-3 w-full lg:w-auto lg:min-w-0">
          <div className="flex flex-row flex-wrap items-center justify-between gap-3">
            {/* View Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-lg w-full sm:w-auto">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveView(tab.id)}
                    className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeView === tab.id
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <IconComponent className="h-4 w-4 shrink-0" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3">
              <Calendar className="h-4 w-4 text-gray-500 shrink-0" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shrink-0"
                aria-label="Month"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shrink-0"
                aria-label="Year"
              >
                {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleSearchClick}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shrink-0"
              >
                <Search className="h-4 w-4" />
                <span>Search</span>
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium shrink-0 ${isExporting
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500'
                }`}
              >
                <Download className="h-4 w-4" />
                <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            {tabs.find(tab => tab.id === activeView)?.description}
          </p>
        </div>
      </div>

      {/* Content - key forces views to remount and refetch when period changes */}
      <div className="relative">
        <p className="px-4 sm:px-6 py-2 text-sm text-gray-600 bg-white border-b border-gray-100">
          Showing data for: <strong>{months.find(m => m.value === appliedMonth)?.label ?? '—'} {appliedYear}</strong>
        </p>
        {activeView === 'operators' && (
          <ByOperatorView
            key={`operators-${appliedMonth}-${appliedYear}`}
            month={appliedMonth}
            year={appliedYear}
          />
        )}
        {activeView === 'services' && (
          <ByServiceView
            key={`services-${appliedMonth}-${appliedYear}`}
            month={appliedMonth}
            year={appliedYear}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;